'use client';

/**
 * TagCloud — multi-field flat tag pill cloud for rail cards.
 *
 * Cards-everywhere-native primitive. Takes a record + a list of field
 * names; flattens scalar/list values across the named fields, dedupes
 * (case-insensitive) preserving first-seen order, renders as pills.
 * Same shape the legacy `<TagsSection>` produces; lives under
 * rail-insights/sections/ so the kill-Copilot sweep doesn't take it.
 *
 * Auto-hides when the flattened tag list is empty.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { text as textStyles } from '@/lib/design/tokens';

interface TagCloudProps {
  data: Record<string, unknown>;
  fields: string[];
  label: string;
}

function toTagList(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.trim());
  }
  return [];
}

export function TagCloud({ data, fields, label }: TagCloudProps) {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const field of fields) {
    for (const tag of toTagList(data[field])) {
      const key = tag.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        tags.push(tag);
      }
    }
  }
  if (tags.length === 0) return null;

  return (
    <div>
      <p className={textStyles.eyebrow}>{label}</p>
      <div className="flex flex-wrap gap-1">
        {tags.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className={`inline-flex items-center rounded-full bg-muted px-2 py-0.5 ${textStyles.muted}`}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}
